import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';

const ScrollableContainer = styled.div`
    position: relative;
    max-height: 100%;
    overflow: hidden;
`;

const ScrollableContent = styled.div`
    max-height: 100%;
    overflow-y: auto;
    overflow-x: hidden;
`;

const ScrollIndicator = styled.div<{ visible: boolean; position: 'top' | 'bottom' }>`
    position: absolute;
    left: 0;
    right: 0;
    height: 8px;
    pointer-events: none;
    z-index: 1;
    opacity: ${(props) => (props.visible ? 1 : 0)};
    transition: opacity 75ms ease-in-out;

    ${(props) =>
        props.position === 'top' &&
        `
        top: 0;
        box-shadow: inset 0 8px 8px -8px rgba(25, 54, 67, 0.15);
    `}

    ${(props) =>
        props.position === 'bottom' &&
        `
        bottom: 0;
        box-shadow: inset 0 -8px 8px -8px rgba(25, 54, 67, 0.15);
    `}
`;

interface ScrollableTableContainerProps {
    children: React.ReactNode;
    scrollToTopTrigger?: any;
}

export const ScrollableTableContainer = ({ children, scrollToTopTrigger }: ScrollableTableContainerProps) => {
    const scrollRef = useRef<HTMLDivElement>(null);
    const [showTopShadow, setShowTopShadow] = useState(false);
    const [showBottomShadow, setShowBottomShadow] = useState(false);

    const handleScroll = () => {
        if (!scrollRef.current) return;

        const { scrollTop, scrollHeight, clientHeight } = scrollRef.current;
        const isScrollable = scrollHeight > clientHeight;
        const isScrolledFromTop = scrollTop > 0;
        const isScrolledToBottom = scrollTop + clientHeight >= scrollHeight - 1; // -1 for rounding

        // Only show shadows when content is actually scrollable
        setShowTopShadow(isScrollable && isScrolledFromTop);
        setShowBottomShadow(isScrollable && !isScrolledToBottom);
    };

    useEffect(() => {
        const scrollElement = scrollRef.current;
        if (!scrollElement) return;

        // Initial check
        handleScroll();

        scrollElement.addEventListener('scroll', handleScroll);

        const resizeObserver = new ResizeObserver(() => {
            handleScroll();
        });
        resizeObserver.observe(scrollElement);

        return () => {
            scrollElement.removeEventListener('scroll', handleScroll);
            resizeObserver.disconnect();
        };
    }, []);

    // Scroll to top when trigger changes (e.g., when filters change)
    useEffect(() => {
        if (scrollRef.current && scrollToTopTrigger !== undefined) {
            scrollRef.current.scrollTop = 0;
        }
    }, [scrollToTopTrigger]);

    return (
        <ScrollableContainer>
            <ScrollIndicator visible={showTopShadow} position="top" />
            <ScrollableContent ref={scrollRef}>{children}</ScrollableContent>
            <ScrollIndicator visible={showBottomShadow} position="bottom" />
        </ScrollableContainer>
    );
};
